/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/
import { Component, inject } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { ReplaySubject, Observable, of, forkJoin } from 'rxjs';
import { first } from 'rxjs/operators';
import { BankAccount, BankItem } from '@src/app/model/bankAccount';
import { Cycle } from '@src/app/model/cycle';
import { Field } from '@src/app/shared/field/Field';
import { Person } from '@src/app/model/person';
import { Txn } from '@src/app/model/txn';
import { Unit } from '@src/app/model/unit';
import { PersonService } from '@src/app/pages/person-page/person.service';
import { AbstractPageComponent } from '@src/app/shared/form/abstract-page.component';
import { AppFormControl } from '@src/app/shared/form/app-form-control';
import { FieldSet, LAYOUT_OPTIONS } from '@src/app/shared/form/field-set/field-set.component';
import { FormButtonComponent } from '@src/app/shared/form/form-button/form-button.component';
import { FormComboBoxComponent } from '@src/app/shared/form/form-combo-box/form-combo-box.component';
import { FormDateComponent } from '@src/app/shared/form/form-date/form-date.component';
import { ErrorCode, FormError } from '@src/app/shared/form/form-error/form-error.component';
import { FormNumberComponent } from '@src/app/shared/form/form-number/form-number.component';
import { AppPicklistControl, FormPicklistComponent } from '@src/app/shared/form/form-picklist/form-picklist.component';
import { FormTextComponent } from '@src/app/shared/form/form-text/form-text.component';
import { FormConfig } from "@src/app/shared/form/FormConfig";
import { GridField } from '@src/app/shared/grid/grid-field';
import { GridRow } from '@src/app/shared/grid/grid-row';
import { GridControl } from '@src/app/shared/grid/grid-control';
import { validateTxnDate, required, minValue, maxValue } from '@src/app/shared/validators';
import { BCodeService } from '@src/app/modules/budget/bcode.service';
import { CycleService } from '@src/app/modules/budget/cycle.service';
import { PeriodService } from '@src/app/modules/budget/period.service';
import { UnitService } from '@src/app/modules/unit/unit.service';
import { CurrentUserService } from '@src/app/modules/user/current-user.service';
import { BankAccountService } from '@src/app/modules/txn/bank-account.service';
import { BankInService } from '@src/app/modules/txn/bank-in.service';
import { MessageService, MsgDef } from '@src/app/shared/message.service';
import { PicklistField } from '@src/app/shared/field/PicklistField';
import { FieldMaker } from '@src/app/shared/field/FieldMaker';
import { AbstractObject, uuid } from '@src/app/model/abstract-object';
import { FormPageComponent } from '@src/app/shared/form/form-page/form-page.component';
import { UnitPageComponent } from '@src/app/modules/unit/unit-page/unit-page.component';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { BankInEditAction } from '@src/app/modules/txn/bank-in-page/bank-in-edit-action';
import { formatCurrency } from '@angular/common';
import { CDSData, ConfirmDialogService } from '@src/app/shared/dialogs/confirmDialog';
import { PaymentDistributor } from '@src/app/modules/txn/bank-in-page/paymentDistributor';
import { FormTextAreaComponent } from '@src/app/shared/form/form-text-area/form-text-area.component';
import { Period } from '@src/app/model/period';
import { FormNavLinkComponent } from '@src/app/shared/form/form-nav-button/form-nav-link.component';
import { ReconciliationDetailAction } from '@src/app/modules/txn/bank-in-page/reconciliationDetailAction';
import { BankInPageNavRoute } from '@src/app/routes/bank-in-page-nav';
import { NrHelper } from '@src/app/shared/NrHelper';
import { BankOutPageNavRoute } from '@src/app/routes/bank-out-page-nav';
import { FieldSetDialogComponent, FieldSetDialogOptions } from '@src/app/shared/dialogs/field-set-dialog/field-set-dialog.component';

export class BankInDialogOptions {
    unit?: Unit;
    person?: Person;
    item?: BankItem;
    invoice?: Txn;
}

@Component({
    selector: 'app-bank-in-page',
    templateUrl: './bank-in-page.component.html',
    styleUrls: ['./bank-in-page.component.scss'],
    imports: [FormPageComponent]
})
export class BankInPageComponent extends AbstractPageComponent {
    dataSvc = inject(BankInService);
    bCodeSvc = inject(BCodeService);
    protected activeRoute = inject(ActivatedRoute);
    private unitSvc = inject(UnitService);
    personSvc = inject(PersonService);
    protected cycleSvc = inject(CycleService);
    protected periodSvc = inject(PeriodService);
    protected bankAccountSvc = inject(BankAccountService);
    private cds = inject(ConfirmDialogService);
    private dialog = inject(MatDialog);
    private currentUserSvc = inject(CurrentUserService);
    dialogRef = inject<MatDialogRef<BankInPageComponent>>(MatDialogRef, { optional: true });
    bankInDialogOptions = inject<BankInDialogOptions>(MAT_DIALOG_DATA, { optional: true });


    personForReceipt: Person = null;

    readonly path = Txn.TYPE.BANK_IN.code

    cycles: Cycle[] = [];
    periods: Period[] = [];

    cycleField: PicklistField = FormPicklistComponent.make('Cycle', 'txnCycleId', 'txnCycle',
        { service: this.cycleSvc }, { formColumn: 2, visible: Field.noShow }
    );

    periodField: Field = FormPicklistComponent.make('Period', 'txnPeriodId', 'txnPeriod',
        { service: this.periodSvc },
        {
            formColumn: 2, visible: Field.noShow,
            refresh: (o: Cycle, control: AppPicklistControl) => { if (o) { control.field.setPicklistItems(o.periods); control.setValue(null); } },
        }
    );

    txnDateField: Field = FormDateComponent.make('Transaction Date', 'txnDate', {
        cellOpts: { width: '2%' },
        validators: [validateTxnDate(this.cycleField, this.periodField)],
        valueChanges: this.dateValueChanges.bind(this)
    });

    amountField: Field = FormNumberComponent.make("Total", "debit",
        { format: 'currency', width: 9, formatParms: '1.2-2' },
        { formColumn: 2, valueChanges: this.totalValueChanges.bind(this), validators: [this.validateAmount.bind(this)] }
    );

    unallocatedField = FormNumberComponent.make("To be allocated", "unallocated",
        { format: 'currency', width: 9, formatParms: '1.2-2' },
        { formColumn: 2, visible: Field.formOnly, disable: true, validators: [minValue(0), maxValue(0)] }
    );

    invoices: Txn[];

    units: Unit[] = [];
    allUnits: Unit[] = [];
    associatedUnitsField = FormTextAreaComponent.make('Associated Units', 'associatedUnits', 
        {sendServer: false, visible: Field.noShow, readonly: true, formColumn: 3})

    childTxn = new GridField({
        field:
        {
            label: 'Allocation', value: 'details',
            visible: Field.formOnly, formRow: 2,
        },
        rowFactory: (o: Txn) => [

            FieldMaker.id(),
            FormTextComponent.make('ledgerId', 'ledgerId', { visible: Field.noShow }),
            FormTextComponent.make('typeId', 'typeId', { visible: Field.noShow }),
            FormTextComponent.make('bCodeId', 'bCodeId', { visible: Field.noShow }),
            FormTextComponent.make('personId', 'personId', { visible: Field.noShow }),

            FormTextComponent.make('txnDate', 'txnDate', { visible: Field.noShow }),
            FormTextComponent.make('txnCycleId', 'txnCycleId', { visible: Field.noShow }),
            FormTextComponent.make('txnPeriodId', 'txnPeriodId', { visible: Field.noShow }),
            FieldMaker.id({ value: 'relatedId', name: 'relatedId' }),
            FieldMaker.idHolder('related.txnCycleId').override({sendServer: false}),
            FieldMaker.idHolder('discountToApply'),
            FormNavLinkComponent.makeTxnLink('Ref', true),
            FormNumberComponent.make("Ref", "relatedRev", {},
                { visible: Field.noShow, cellOpts: { width: "3em", maxWidth: "3em" } }
            ),
            FormDateComponent.make('Date', 'related.txnDate',
                { sendServer: false, disable: true, cellOpts: { minWidth: "6em" } }
            ),
            FormPicklistComponent.make('Type', 'related.typeId', 'related.type',
                { items: Txn.TYPES },
                { sendServer: false, disable: true, cellOpts: { minWidth: "9em" } }
            ),

            FieldMaker.idHolder('unitId'),
            FormButtonComponent.makeTextButton('Unit', 'unitName', () => {
                this.dialog.open(UnitPageComponent, {data: {id: o.unitId}});
            }),


            FormNumberComponent.make("Amount", 'related.debit',
                { format: 'currency', width: 9, formatParms: '1.2-2' },
                { cellOpts: { minWidth: "6em", maxWidth: "6em" }, sendServer: false, disable: true }
            ),
            FormNumberComponent.make("Outstanding", "related.outstanding",
                { format: 'currency', width: 9, formatParms: '1.2-2' },
                { cellOpts: { minWidth: "6em", maxWidth: "6em" }, sendServer: false, disable: true }
            ),
            FormNumberComponent.make("Allocate", "credit",
                { format: 'currency', width: 9, formatParms: '1.2-2' },
                { valueChanges: this.totalValueChanges.bind(this) }
            ),
            FormButtonComponent.make('Pay', '', {
                name: 'action', label: '', type: 'icon', sendServer: false,
                cellOpts: { heading: '', width: '1%' }, disable: o?.related?.outstanding === 0,
                calculateValue: () => 'assignment_turned_in',
                toolTip: 'Click to pay this item in full',
                btnOpts: { clickMethod: this.allocatePayment.bind(this) }
            }),

            FieldMaker.notes({ cellOpts: { width: "50%" } }),
            FieldMaker.rev(),
            FieldMaker.deleteGridRow({ clickMethod: this.removeAllocationRow.bind(this) }),
        ]
    });

    configReady = new ReplaySubject<null>(1);
    configSetup = false;
    people: Person[] = [];

    personPickerField = FormComboBoxComponent.make('Contact', 'personId', 'person',
        {
            items: [],
            optionDisplayValue: (o: Person) => o?.fullName,
            allowSelectNone: true,
            refreshes: [this.refreshUnitList.bind(this), this.saveReceiptPerson.bind(this)]
        }, { formColumn: 3}
    );

    distributeField = FormButtonComponent.makeIconButton('Distribute', 'vertical_distribute', 'Distribute amount evenly over units',
        this.distributePayments.bind(this)).override({formColumn: 3, visible: Field.noShow });

    unitPickerField = FormComboBoxComponent.make('Unit', 'unitId', 'unit',
        { items: [], refreshes: [this.refreshInvoices.bind(this), this.refreshContactList.bind(this)], optionDisplayValue: (o: Unit) => Unit.getFullName(o) },
        { sendServer: false, formColumn: 3}
    );

    refNrField = FormNumberComponent.make('refNr', 'refNr', { format: 'plain' });

    reconciledField = FormTextComponent.make('Reconciled', 'isReconciled', {
        calculateValue: (o:Txn) => o.bankItemId ? $localize `reconciled` : $localize `*** Unreconciled ***`,
        formColumn: 2,
        tableStyle: (o:Txn) => (o.bankItemId ? {} : {color:'red'}),
    })

    bankAccountField = FormPicklistComponent.make('Bank Account', 'bankAccountId', 'bankAccount',
                    { service: this.bankAccountSvc, refreshes: ['bCodeId'] },
                    { validators: [required] }
    );

    /* Months along the bottom of the receipts charts, years as the series - see the nav route */
    monthField = Txn.makeMonthField();
    yearField = Txn.makeYearField();

    config = new FormConfig({
        navRoute: BankInPageNavRoute,
        docPath: 'omc-books/service-charges/bank-receipts',
        title: Txn.TYPE.BANK_IN.name,
        help: $localize`Financial transactions`,
        readonly: true,
        fieldSet: new FieldSet({
            fields: [
                FieldMaker.id(),
                FieldMaker.rev(),
                FieldMaker.idHolder('bankItemId'),
                this.refNrField,
                FormTextComponent.make('ledgerId', 'ledgerId', { visible: Field.noShow }),
                FormTextComponent.make('typeId', 'typeId', { visible: Field.noShow }),
                this.bankAccountField,
                FormPicklistComponent.make('Account', 'bCodeId', 'bCode', { service: this.bCodeSvc },
                    {
                        formColumn: 3, validators: [required], visible: Field.noShow,
                        refresh: (o: BankAccount, control) => { control.setValue(o?.bCodeId); }
                    }
                ),

                /* txtDateField Validator overwrites period info, need this order! */
                this.cycleField,
                this.periodField,
                this.txnDateField,
                this.monthField,
                this.yearField,
                this.amountField,
                this.unallocatedField,
                this.reconciledField,
                this.personPickerField,
                this.distributeField,
                this.unitPickerField,
                this.associatedUnitsField,
                FieldMaker.notes({ formColumn: 4 }),
                this.childTxn,
            ],
            formValidator: this.validatePayment.bind(this),
            formLayout: [
                { cells: [{ width: '25%' }, { width: '25%' }, { width: '25%' }, { width: '25%' }] },
                { cells: [{ colspan: 4, width: '90%', pageTab: 'yes' }] }
            ],
        }),
        service: this.dataSvc,
        mode: 'list',
        objectFactory: this.newFactory.bind(this),
        configReady: this.configReady,
        beforeEdit: this.enhanceObject.bind(this),
        getConfirmSaveMessage: this.getConfirmSaveMessage.bind(this),
        newOptions: [
            { name: $localize`New ${Txn.TYPE.BANK_IN.name}`, basePath: BankInPageNavRoute.newUrl, params: {}, id:'newReceipt' },
            { name: $localize`New ${Txn.TYPE.BANK_OUT.name}`, basePath: BankOutPageNavRoute.newUrl, params: {}, id:'newPayment' },
        ],
        pathEdit: (o: Txn) => Txn.getTxnLink(o),
        actions: [new BankInEditAction(), new ReconciliationDetailAction()]
    });

    defaultBank: BankAccount = null;

    constructor() {
        const msgSvc = inject(MessageService);


        super();
        const unitSvc = this.unitSvc;
        const personSvc = this.personSvc;
        const cycleSvc = this.cycleSvc;
        const periodSvc = this.periodSvc;
        const currentUserSvc = this.currentUserSvc;
    

        forkJoin({
            currentUser: currentUserSvc.getCurrentUser().pipe(first()), 
            bankAccounts: this.bankAccountSvc.get(true).pipe(first()),
            units: unitSvc.get(true).pipe(first()),
            cycles: cycleSvc.get<Cycle>(true).pipe(first()),
            defaultBCodes: currentUserSvc.getDefaultBCodes().pipe(first()),
            periods: periodSvc.get<Period>(true),
            people: personSvc.get<Person>(true)
        }).subscribe(
            (result) => {
                this.cycles = result.cycles;
                this.periods = result.periods
                if (result.bankAccounts.length === 0) {
                    msgSvc.show(new MsgDef($localize`No bank accounts setup, you will not be able to enter receipts`, 'warn'));
                } else {
                    for (const acct of (result.bankAccounts as BankAccount[])) {
                        if (acct.defaultAcct) {
                            this.defaultBank = acct;
                        }
                    }

                    result.units.forEach( (u: Unit) => this.allUnits.push(u));
                    this.units = this.allUnits.slice();
                    this.unitPickerField.setPicklistItems(result.units);
                }
                this.people = result.people;
                this.setAllContacts();
                this.currentUser = result.currentUser;
                this.configReady.next(null);
                this.configSetup = true;
            }
        );
    }

    distributePayments() {
        const amt = this.amountField.control.value;
        const units = this.unitPickerField.picklist.items as Unit[];
        const personId = this.personPickerField.control.value;
        new PaymentDistributor(amt, personId, units, this.createAllocation.bind(this));
    }

    getConfirmSaveMessage(): string {
        if (!this.personForReceipt) {
            return $localize`Save without sending a receipt, cancel and select person to send receipt`;
        } else if (!this.personForReceipt.email) {
            return $localize`${this.personForReceipt.fullName} has no email address, will not be able to send receipt, save anyway?`;
        }
        return $localize`Save and send receipt to ${this.personForReceipt.fullName} (${this.personForReceipt.email})?`;
    }

    validateAmount(control: AppFormControl) {
        if (this.bankInDialogOptions?.item) {
            const amt = formatCurrency(this.bankInDialogOptions.item.transactionAmount,'EN-ie', '€');
            if (!NrHelper.equal(control.value, this.bankInDialogOptions.item.transactionAmount)) {
                return new ErrorCode('invalid', $localize `${amt} expected to match bank item`)
            }
        }
        return null
    }

    enhanceObject(o: Txn) {
        this.config.readonly = true;
        this.refNrField.visible = Field.showAll;
        this.unitPickerField.visible = Field.noShow;
        this.distributeField.visible = Field.noShow;
        this.associatedUnitsField.visible = Field.formOnly;
        this.reconciledField.visible = Field.formOnly;
        const units = [];
        o.details.forEach( dtl => {
            if (units.indexOf(dtl.unitName) < 0 ) {
                units.push(dtl.unitName);
            }
        })
        if (units.length === 1) {
            o.associatedUnits = units[0]
        } else {
            o.associatedUnits = $localize `${units.length} Units - ${units.join(', ')} ` /* `${units.length}: ${units.join(', ')}`; */
        }
        
        o.details = o.details.filter( t => t.typeId !== Txn.TYPE.BANK_IN_DISCOUNT.id);
        return o;
    }

    validatePayment() {

        if (this.childTxn.control && this.childTxn.control.controls.length === 0) {
            return FormError.reportError('paymentLineItems', $localize`Select person and/or unit to see charges to allocate payment against`);
        }

        const ufc = this.unallocatedField.control;
        if (ufc && ufc.value > 0.0000001) {
            return FormError.reportError('paymentUnallocated',
                $localize`Allocate payment or put on account the remaining ${ufc.field.formatValue(ufc.value)}`);
        }

        return null;
    }

    refreshUnitList(o: Person) { //, ctl: AppPicklistControl
        const personUnits: Unit[] = [];
        if (o && o.id) {
            this.personSvc.getOne(o.id, new Person()).pipe(first()).subscribe(person => {
                for (const pur of person.personUnits) {
                    if (pur.unit) {
                        personUnits.push(pur.unit);
                    }
                }
                this.childTxn.deleteAll();
                this.unitPickerField.clearComboValue(false);
                this.unitPickerField.setPicklistItems(personUnits);
                this.distributeField.visible = Field.noShow;
                if (personUnits.length === 1) {
                    this.unitPickerField.setValue({unitId: personUnits[0].id, unit: personUnits[0]} as unknown as AbstractObject);
                    this.refreshInvoices(personUnits[0]);
                } else if (personUnits.length === 0) {
                    const title = $localize`No units associated with contact`;
                    const msg = $localize`The selected contact is not associated with any units, cannot allocate payment without a unit.`;
                    this.cds.alert(title, msg);
                } else {
                    this.distributeField.visible = Field.formOnly;
                    this.distributeField.label = $localize`Distribute over ${personUnits.length} units`;
                }
            });
        } else {
            this.unitPickerField.setPicklistItems(this.allUnits);
        }
    }

    refreshContactList(u: Unit) {
        this.personPickerField.clearComboValue(false);
        this.saveReceiptPerson(null);
        if (u && u.id) {
            this.setUnitOwnerContacts(u.id);
        } else {
            this.setAllContacts();
        }
    }

    saveReceiptPerson(o: Person) {
        if (o && o.id) {
            this.personForReceipt = o;
        } else {
            this.personForReceipt = null;
        }
    }

    allocatePaymentToRow(gridRow: GridRow, maxAllocation: number = null) {
        const outstanding = gridRow.get('related_outstanding');
        const credit = gridRow.get('credit');
        const notes = gridRow.get('notes');
        const discountToApply = gridRow.get('discountToApply');
        const unallocated = this.page.form.formGroup.get('unallocated').value + credit.value;
        const actionBtn = (gridRow.get('action') as AppFormControl);


        const cycleId = gridRow.get('related_txnCycleId').value;
        const origAmt = gridRow.get('related_debit').value;
        const cycle = this.cycles.find(c => c.id === cycleId);
        const fullPayDiscount = cycle?.fullPayBy >= this.txnDateField.control.value ? cycle.fullPayDiscount : 0;
        const settlementDiscount = Math.round(origAmt * fullPayDiscount * 100) / 100;
        const relatedId = gridRow.get('relatedId').value;

        if (actionBtn.value === 'backspace') {
            credit.setValue(0);
        } else {
            if (unallocated > 0 && outstanding.value > 0) {
                const toBePaid = Math.max(outstanding.value - settlementDiscount, 0);
                const beingPaid = Math.min(toBePaid, unallocated, maxAllocation ?? toBePaid);
                credit.setValue(beingPaid);

                if (Math.abs(outstanding.value - settlementDiscount - beingPaid) < 0.01
                && settlementDiscount > 0.0001) {
                    notes.setValue('Discount of €' + settlementDiscount + ' to be applied');
                    discountToApply.setValue(settlementDiscount);
                }
            } else if (outstanding.value < 0) {
                credit.setValue(outstanding.value);
            } else if (relatedId === null) {
                credit.setValue(unallocated);
            }
        }
    }

    setPaymentIconOnRow(gridRow: GridRow) {
        const outstanding = gridRow.get('related_outstanding').value;
        const credit = gridRow.get('credit').value;
        const unallocated = this.page.form.formGroup.get('unallocated').value;
        const actionBtn = (gridRow.get('action') as AppFormControl);

        if (credit < outstanding && unallocated > 0) {
            actionBtn.setValue('assignment_turned_in');
            actionBtn.field.disable = false;
            actionBtn.field.toolTip = 'Click to pay this item in full';
        } else if (credit > 0) {
            actionBtn.setValue('backspace');
            actionBtn.field.disable = false;
            actionBtn.field.toolTip = 'Click to remove payment allocation';
        } else {
            actionBtn.setValue('assignment_turned_in')
            if (gridRow.get('relatedId').value === null && unallocated > 0) {
                actionBtn.field.disable = false;
                actionBtn.field.toolTip = 'Click to take as payment on account';
            } else {
                actionBtn.field.disable = true;
                actionBtn.field.toolTip = 'No monies available to allocate';
            }
        }
    }

    allocatePayment(ctl: AppFormControl): void {
        if (ctl.isGridCell) {
            this.allocatePaymentToRow(ctl.getRow());
        }
    }

    removeAllocationRow(ctl: AppFormControl): void {
        if (ctl.isGridCell) {
            ctl.getRow().delete();
            this.totalValueChanges();
        }
    }

    dateValueChanges(): void {
        const allocations = (this.childTxn.control as GridControl).controls;
        if (allocations) {
            for (const txnRow of allocations) {
                txnRow.get('txnDate').setValue(this.txnDateField.control.value, { emitEvent: false });
                txnRow.get('txnCycleId').setValue(this.cycleField.control.value, { emitEvent: false });
                txnRow.get('txnPeriodId').setValue(this.periodField.control.value, { emitEvent: false });
            }
        }
    }

    totalValueChanges(): void {
        const fg: UntypedFormGroup = this.page.form.formGroup;
        const debit = fg.get('debit').value;
        let allocated = 0;
        const allocations = (this.childTxn.control as GridControl).controls;
        if (allocations) {
            for (const txnRow of allocations) {
                allocated += txnRow.get('credit').value;
            }
        }

        this.unallocatedField.control.setValue(debit - allocated, { emitEvent: false });
        for (const txnRow of allocations) {
            this.setPaymentIconOnRow((txnRow as GridRow));
        }
    }

    getCurrentPersonId(): number {
        const personChosen = this.page.form.formGroup.get('personId').value;
        if (personChosen) {
            return personChosen;
        } else {
            return null;
        }
    }

    /**
     * Sorts transactions in date order to pay oldest first
     * @param a Txn
     * @param b
     * @returns
     */
    txnSorter(a: Txn, b: Txn) {
        if (a.txnDate > b.txnDate) {
            return 1; // > 0 - Put B before A
        } else if (a.txnDate < b.txnDate) {
            return -1 // < 0 put A before B
        } else {
            // Same Date, go by order created, which is ID
            if (a.id > b.id) {
                return 1; // B was created before A, put B first
            } else {
                return -1 // A was created first.
            }
        }
    }

    refreshInvoices(unit: Unit) {
        const personId = this.getCurrentPersonId();

        this.childTxn.deleteWhere( gr => {
            if(gr.get('credit').value === 0) {
                return true;
            }
            return false;
        });
        if (this.childTxn.control.gridRows().length > 0) {
            const title = $localize `Keep Allocations`;
            const msg = $localize `
                Money has been allocated for units already, do you want to keep these allocations or start fresh?
                If you are uncertain, start fresh. You would only keep the existing allocations if you are deliberately
                entering a receipt where money needs to be allocated against more then one unit.
            `;
            const cdsData = {
                title, msg, options: [
                    { 
                        name: $localize `Keep`, action: () => this.showInvoicesForUnit(unit, personId)
                    }, { 
                        name: $localize `Start Fresh`, action: () => {
                            this.childTxn.deleteWhere( () => true);                            
                            this.showInvoicesForUnit(unit, personId);
                        }
                    },
                ]
            } as CDSData;
            this.cds.openChoice(cdsData, false);
        } else {
            this.showInvoicesForUnit(unit, personId);
        }
    }

    showInvoicesForUnit(unit: Unit, personId: uuid) {
        if (unit?.id) {
            this.dataSvc.getOutstanding(unit.id).subscribe(invoices => {
                const openTrans = invoices.sort(this.txnSorter); // Txn.TYPE.BANK_IN_ON_ACCT.id
                const credits = openTrans.filter(o => o.outstanding < 0);
                for (const inv of credits) {
                    this.createAllocation(inv, unit, personId);
                }
                const debits = openTrans.filter(o => o.outstanding > 0);
                for (const inv of debits) {
                    this.createAllocation(inv, unit, personId);
                }
                this.createUnallocated(unit, personId);
            });
        }
    }

    createAllocation(inv: Txn, unit: Unit, personId: uuid, maxAllocation: number = null) {
        this.childTxn.control.gridRows().forEach( gr => {
            if (gr.get('relatedId').value === inv.id) {
                return;
            }
        })

        const newTxn = this.newAllocationTxn(unit, personId);
        newTxn.relatedId = inv.id;
        newTxn.relatedRev = inv.revision;
        newTxn.related = (inv as Txn);
        const newRow: GridRow = this.childTxn.control.addRow(newTxn, true, false);
        this.allocatePaymentToRow(newRow, maxAllocation);
    }

    createUnallocated(unit: Unit, personId: uuid) {
        this.childTxn.control.gridRows().forEach( gr => {
            if (gr.get('unitId').value === unit.id && gr.get('typeId').value === Txn.TYPE.BANK_IN_ON_ACCT.id) {
                return;
            }
        })
        const newTxn = this.newAllocationTxn(unit, personId);
        newTxn.notes = 'Unallocated on account';
        newTxn.bCodeId = this.currentUserSvc.getDefaultBCode('Debtors').bCodeId;
        newTxn.typeId = Txn.TYPE.BANK_IN_ON_ACCT.id;
        newTxn.type = Txn.TYPE.BANK_IN_ON_ACCT;
        newTxn.relatedId = null;
        newTxn.related = new Txn();
        newTxn.related.unitId = unit.id;
        newTxn.related.unit = unit;
        newTxn.related.typeId = Txn.TYPE.BANK_IN.id;
        newTxn.related.type = Txn.TYPE.BANK_IN;
        const newRow = this.childTxn.control.addRow(newTxn, true, false);
        this.allocatePaymentToRow(newRow);
    }

    newAllocationTxn(unit: Unit, personId): Txn {
        const newTxn = new Txn();
        newTxn.ledgerId = Txn.LEDGER.AR.id;
        newTxn.ledger = Txn.LEDGER.AR;
        newTxn.typeId = Txn.TYPE.BANK_IN_ALLOC.id;
        newTxn.type = Txn.TYPE.BANK_IN_ALLOC;
        newTxn.bCodeId = this.currentUserSvc.getDefaultBCode('Debtors').bCodeId;
        newTxn.txnDate = this.txnDateField.control.value;
        newTxn.txnCycleId = this.cycleField.control.value;
        newTxn.txnPeriodId = this.periodField.control.value;
        newTxn.unitId = unit.id;
        newTxn.unitName = unit.name
        newTxn.personId = personId;

        return newTxn;
    }

    setAllContacts() {
        this.personPickerField.setPicklistItems(this.people);
        this.personPickerField.picklist.refreshes = [this.refreshUnitList.bind(this), this.saveReceiptPerson.bind(this)];
    }

    setUnitOwnerContacts(unitId: uuid) {
        this.unitSvc.getOwners(unitId).subscribe ( owners => {
            if (owners) {
                const people = [];
                let lastPerson : Person;
                owners.forEach( o => {
                    lastPerson = {id: o.personId, fullName: o.personFullName, email: o.personEmail} as Person;
                    people.push(lastPerson);
                })

                this.personPickerField.setPicklistItems(people);

                this.personPickerField.picklist.refreshes = [this.saveReceiptPerson.bind(this)];
                if (owners.length === 1) {
                    // txn loaded before owners come back...
                    this.personPickerField.control.setValue(lastPerson.id);
                    this.personPickerField.setComboDisplayValue(lastPerson);
                    this.saveReceiptPerson(lastPerson);
                }
            }
        })        
    }

    newFactory(): Observable<Txn> {
        const txn = new Txn();
        this.personForReceipt = null;
        this.refNrField.visible = Field.noShow;

        txn.ledgerId = Txn.LEDGER.AR.id;
        txn.ledger = Txn.LEDGER.AR;

        txn.typeId = Txn.TYPE.BANK_IN.id;
        txn.type = Txn.TYPE.BANK_IN;

        if (this.defaultBank) {
            txn.bankAccountId = this.defaultBank.id;
            txn.bankAccount = this.defaultBank;

            txn.bCodeId = this.defaultBank.bCodeId;
        }
        this.setAllContacts();
        //txn.txnDate = moment().toISOString().substring(0, 10);
        //validateTxnDate(this.cycleField, this.periodField)(this.)

        this.config.readonly = false;
        this.associatedUnitsField.visible = Field.noShow;
        this.unitPickerField.visible = Field.formOnly;
        this.reconciledField.visible = Field.noShow;

        if (!this.bankInDialogOptions && this.activeRoute.snapshot.params.personId) {
            console.warn('How is this called, not with proper navRoute anyway');
            txn.personId = this.activeRoute.snapshot.params.personId;
            window.setTimeout(() => {
                const p = this.personPickerField.picklist.items.find(p => p.id === txn.personId) as Person;
                this.refreshUnitList(p);
                this.saveReceiptPerson(p);
            });
        }
        if (!this.bankInDialogOptions) {
            return this.getReceiptDetails(txn);
        }

        if (this.bankInDialogOptions) {
            if (this.bankInDialogOptions.item) {
                txn.debit = this.bankInDialogOptions.item.transactionAmount;
                txn.bankItemId = this.bankInDialogOptions.item.id;
                this.setTxnDate(txn, this.bankInDialogOptions.item.bookingDate);
                /*
                const prd = this.periodSvc.getOpenPeriod(txn.txnDate, this.periods)
                if (prd) {
                    txn.txnPeriodId = prd.id;
                    txn.txnCycleId = prd.cycleId;
                }
                */
                this.txnDateField.disableControl();
            }
            if (this.bankInDialogOptions.unit || this.bankInDialogOptions.invoice) {
                if (this.bankInDialogOptions.unit) {
                    txn.unitId = this.bankInDialogOptions.unit.id;
                } else if (this.bankInDialogOptions.invoice){
                    txn.unitId = this.bankInDialogOptions.invoice.unitId;
                }

                this.unitPickerField.disableControl();
                this.setUnitOwnerContacts(txn.unitId);
            }
            if (this.bankInDialogOptions.person) {
                txn.personId = this.bankInDialogOptions.person.id;
                this.personPickerField.disableControl();
            }

            window.setTimeout(() => {
                if (this.bankInDialogOptions.unit) {
                    this.refreshInvoices(this.bankInDialogOptions.unit)
                } else if (this.bankInDialogOptions.invoice) {
                    this.refreshInvoices({ id: this.bankInDialogOptions.invoice.unitId} as Unit);
                }
                if (this.bankInDialogOptions.person) {
                    this.refreshUnitList(this.bankInDialogOptions.person)
                    this.saveReceiptPerson(this.bankInDialogOptions.person)
                }

                this.totalValueChanges();

                this.amountField.control.markAsDirty();
                this.amountField.control.markAsTouched();
            }, 50);
        }

        return of(txn);
    }

    setTxnDate(txn: Txn, txnDate: string) {
        txn.txnDate = txnDate;
        const prd = this.periodSvc.getOpenPeriod(txn.txnDate, this.periods)
        if (prd) {
            txn.txnPeriodId = prd.id;
            txn.txnPeriod = prd;
            txn.txnCycleId = prd.cycleId;
        }        
    }

    getReceiptDetails(txn: Txn) {

        const dateField = FormDateComponent.make('Transaction Date', 'txnDate', {
            validators: [validateTxnDate(this.cycleField, this.periodField)],
        });
        const amtField = FormNumberComponent.makeCurrency("Amount", "debit")   
        
        const fieldSet = new FieldSet({
            fields: [dateField, amtField ],
            formLayout: LAYOUT_OPTIONS.simpleDialog
        });
        fieldSet.getFormGroup();     

        const data = {
            fieldSet,
            saveFn: this.saveOne(txn),
            cancelValue: txn,
            saveText: $localize`Allocate Monies`,
            title: $localize`Receipt Details Units`
        } as FieldSetDialogOptions

        return this.dialog.open(FieldSetDialogComponent, { data }).afterClosed();
    }

    saveOne(txn: Partial<Txn>) {
        return (txnEdits: Partial<Txn>) => {
            this.setTxnDate(txn as Txn, txnEdits.txnDate);
            txn.txnDate = txnEdits.txnDate;
            txn.debit = txnEdits.debit;
            txn.unallocated = txnEdits.debit;
            console.log(txn);
            return of(txn);
        }
        
    }
}
